import { authedFetch } from './authedFetch';
import type { SiteDocument } from '../types/siteRag';

export interface SiteDocumentOriginalContext { ownerUid: string; siteId: string }
export type DocumentBinaryLoader = (document: SiteDocument, signal?: AbortSignal) => Promise<Blob>;

/** Context supplies identifiers, never a storage path or authority. The server
 * admits the current owner/admin and verifies the canonical original bytes. */
export function createSiteDocumentOriginalLoader(context: SiteDocumentOriginalContext): DocumentBinaryLoader {
  return async (document, signal) => {
    const validId = (value: string) => /^[A-Za-z0-9_-]{1,160}$/.test(value);
    if (!validId(context.ownerUid) || !validId(context.siteId) || !validId(document.id))
      throw new Error('Invalid site document identity.');
    if (!document.rawStoragePath) throw new Error('No original file is stored for this document.');
    if (document.preview?.pdfPath && document.rawStoragePath === document.preview.pdfPath)
      throw new Error('Derived previews require their separate owner-scoped byte path.');
    const { auth } = await import('../firebase');
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error('Sign in to read this site document.');
    // Upfront same-owner selection preserves legacy originals without a
    // complete index/hash and uploader-owned documents whose raw bytes live
    // in the signed-in user's own namespace. This is never a fallback after a site denial.
    const ownRawPrefix = `users/${uid}/documents/${document.id}/raw/`;
    const ownerRead = (uid === context.ownerUid && document.rawStoragePath.startsWith(`users/${uid}/documents/`))
      || (document.rawStoragePath.startsWith(ownRawPrefix)
        && document.rawStoragePath.length > ownRawPrefix.length
        && (!document.storagePath || document.storagePath === `users/${uid}/documents/${document.id}.json`));
    const selectedHash = document.contentHash?.toLowerCase();
    if (!ownerRead && (!/^[a-f0-9]{64}$/.test(selectedHash ?? '')
      || !Number.isSafeInteger(document.sizeBytes) || document.sizeBytes <= 0))
      throw new Error('Original source identity is incomplete. Reload the document library.');
    const controller = new AbortController();
    const abort = () => controller.abort();
    signal?.addEventListener('abort', abort, { once: true });
    if (signal?.aborted) controller.abort();
    let unsubscribe: (() => void) | undefined;
    try {
      const { onAuthStateChanged } = await import('firebase/auth');
      unsubscribe = onAuthStateChanged(auth, user => { if (user?.uid !== uid) controller.abort(); });
      if (auth.currentUser?.uid !== uid) controller.abort();
      const response = ownerRead
        ? await authedFetch(`/api/storage/download-file?path=${encodeURIComponent(document.rawStoragePath)}`, { signal: controller.signal })
        : await authedFetch('/api/site-document-original', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, signal: controller.signal,
          body: JSON.stringify({ ownerUid: context.ownerUid, siteId: context.siteId, documentId: document.id }),
        });
      if (!response.ok) throw new Error(`Download failed (${response.status})`);
      if (!ownerRead && (response.headers.get('X-Document-Sha256') !== selectedHash
        || response.headers.get('X-Document-Byte-Length') !== String(document.sizeBytes)))
        throw new Error('The original file changed. Reload the document library before opening it.');
      const blob = await response.blob();
      if (!ownerRead && blob.size !== document.sizeBytes)
        throw new Error('Original file size does not match the selected document.');
      if (controller.signal.aborted || auth.currentUser?.uid !== uid)
        throw new DOMException('The document read was cancelled.', 'AbortError');
      return blob;
    } finally {
      unsubscribe?.();
      signal?.removeEventListener('abort', abort);
    }
  };
}
